import { CardBrand } from '@afterpay/types'
import { IconSizes, Span, useIsDesignLanguage, DesignLanguage, useDesignLanguagePerClass } from '@afterpaytouch/core'
import clsx from 'clsx'
import React, { FunctionComponent } from 'react'
import { CardErrorReason } from '../../model'
import { CardDetails } from '@afterpaytouch/portal-api'
import { useCheckoutColor } from '../../utils/color'
import { useCheckoutSizes } from '../../utils/responsive'
import { getLast } from '@afterpay/utils/string'
import { CardAsterisk } from '../CardAsterisk'
import { PaymentMethodIcon } from '../PaymentMethodIcon'
import styles from './style.module.css'
import { useTranslation } from '../../utils/convergence'
import { getPaymentMethodCardErrorMessage } from '../../utils/translation-helpers'

interface Props {
  card?: Omit<CardDetails, 'cardBrand'> & { cardBrand?: CardDetails['cardBrand'] }
  reversed?: boolean
  iconSize?: IconSizes
  errorKey?: CardErrorReason | null
  disabled?: boolean
  showErrorMessage?: boolean
}

export const PaymentMethodCard: FunctionComponent<Props> = ({ card, iconSize, errorKey, reversed = false, disabled = false, showErrorMessage = true }) => {
  const error = errorKey !== undefined && errorKey !== null
  const { t } = useTranslation()
  const { smallTextSize } = useCheckoutSizes()
  const designLanguageStyles = useDesignLanguagePerClass(styles)
  const containerClasses = clsx(styles.container, reversed && styles.reversed)
  const cardDetailsContainer = clsx(styles.cardDetailsContainer, designLanguageStyles.cardDetailsContainer)
  const charactersToRender = card?.cardBrand === CardBrand.AMEX ? 3 : 4
  const { textColor, cardExpiryDateColor } = useCheckoutColor({ hasError: error, isDisabled: disabled })
  const cardExpiryLimitInMonths = 2
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)

  const renderErrorMessage = (): string =>
    getPaymentMethodCardErrorMessage({
      t,
      cardError: errorKey,
      cardBrand: card?.cardBrand,
      cardExpiryLimitInMonths,
    })

  return (
    <span className={containerClasses}>
      <span className={styles.icon}>
        <PaymentMethodIcon type={card?.cardBrand} size={iconSize} />
      </span>
      <span className={cardDetailsContainer}>
        <span className={styles.cardDetails}>
          <span>
            <CardAsterisk disabled={disabled} error={error} />
          </span>
          <span className={styles.cardMaskedPan}>
            <Span size='M' color={textColor} bold={isArcadeDesignLanguage}>
              {getLast(card?.maskedPan, charactersToRender)}
            </Span>
          </span>
          <span className={styles.cardExpiryDate}>
            <Span size='M' color={cardExpiryDateColor}>
              {card?.expiryDate}
            </Span>
          </span>
        </span>
        <span>
          {error && !disabled && showErrorMessage && (
            <Span size={smallTextSize} color='Fire'>
              {renderErrorMessage()}
            </Span>
          )}
        </span>
      </span>
    </span>
  )
}
